/**
 * "Redo the kit with the new cover" (owner request 2026-10-16; design
 * `docs/superpowers/design-reference/kit-redo-cover/`). Each made kit asset and each AI reel
 * stores the cover that its render used (`coverRef`). When the song has another cover now, the
 * asset is out of date, and the kit page offers one action that makes every such file again with
 * the new cover.
 *
 * The contract of the two routes (music24-api `src/kit/coverRedo/`):
 * - `GET  /accounts/:accountId/release-kits/:catalogItemUuid/cover-redo` → `CoverRedoState`
 * - `POST /accounts/:accountId/release-kits/:catalogItemUuid/cover-redo` with `CoverRedoRequest`:
 *   `dryRun: true` → 200 `CoverRedoState` for the chosen kinds (nothing is charged or made); else
 *   201 `CoverRedoResult`. When the redo costs credits, the body must carry `expectedCredits` equal
 *   to the cost of the dry run, else 409 `M24_COVER_REDO_CONFIRM` with `cause.credits` (the cost
 *   now). No credit is ever taken without that confirm.
 *
 * The web app, the native apps and the MCP tools read these types. The file imports only
 * `studioThemes.ts`, so every project can use it.
 */
import { isStudioThemeId, STUDIO_THEMES, type StudioThemeId } from './studioThemes';

/** The groups of the confirm sheet, in the order of the sheet. */
export const COVER_REDO_KINDS = [
  'images',
  'coverSizes',
  'linkPreview',
  'shortVideos',
  'fullVideos',
  'musicVideo',
  'aiReels',
] as const;
export type CoverRedoKind = (typeof COVER_REDO_KINDS)[number];

/** The group of an asset type: the cover sizes (the crops), the link preview and the music video have their own. Pure. */
export function coverRedoKindOf(themeId: StudioThemeId): CoverRedoKind {
  if (themeId === 'aiReel') {
    return 'aiReels';
  }
  if (themeId === 'coverSizes' || themeId === 'linkPreview' || themeId === 'musicVideo') {
    return themeId;
  }
  const { format } = STUDIO_THEMES[themeId];
  if (format === 'image') {
    return 'images';
  }
  return format === 'short' ? 'shortVideos' : 'fullVideos';
}

/**
 * True when the file of a kit asset shows the cover or takes its colours from it. Every asset type
 * does, except the clean music video (footage only, `params.musicVideoMode` is not `songInfo`) and
 * a Spotify header made from a photo, a bank picture or an AI picture (`params.headerSource`; the
 * blurred cover and the theme colours read the cover). An AI reel shows the cover on its end card.
 * Pure.
 */
export function kitAssetUsesCover(themeId: string, params: unknown): boolean {
  if (!isStudioThemeId(themeId)) {
    return false;
  }
  const record =
    params !== null && typeof params === 'object' && !Array.isArray(params) ? (params as Record<string, unknown>) : {};
  if (themeId === 'spotifyHeader') {
    return record.headerSource !== 'photo' && record.headerSource !== 'bank' && record.headerSource !== 'ai';
  }
  if (themeId !== 'musicVideo') {
    return true;
  }
  return record.musicVideoMode === 'songInfo';
}

/** Why a file of the redo is not made again. */
export type CoverRedoSkipReason =
  /** The plan of the workspace cannot make this asset type now (a full video on Artist, AI footage). */
  | 'needsPlan'
  /** The asset type reads the song, and the song has no ready master. */
  | 'needsMaster'
  | 'masterProcessing'
  /** A new version of this file is being made now. */
  | 'busy'
  /** No stored plan or cut is left to make it from. */
  | 'failed';

/** One out-of-date file: a current kit asset or an AI reel. */
export interface CoverRedoItem {
  source: 'kitAsset' | 'reel';
  /** The kit asset uuid or the reel uuid of the file that has the old cover. */
  uuid: string;
  kind: CoverRedoKind;
  themeId: StudioThemeId;
  variant: string;
  /** The video credits of its redo; images 0. */
  credits: number;
  /** A picture of the file as it is now (the poster of a video, else the image), or null. */
  thumbUrl: string | null;
  /** Set when the redo leaves this file out; such a file costs nothing and is not in `total`. */
  skipReason: CoverRedoSkipReason | null;
}

/** A post that did not go out and uses an out-of-date file. A posted post never changes. */
export interface CoverRedoPost {
  postUuid: string;
  platform: string;
  /** ISO time. */
  scheduledAt: string;
  status: 'draft' | 'scheduled' | 'awaitingManualPost';
  /** The uuid of the out-of-date file (`CoverRedoItem.uuid`). */
  fileUuid: string;
  kind: CoverRedoKind;
}

/** The newest redo of the song, so the kit page shows its progress, its end and its failures. */
export interface CoverRedoRun {
  runId: string;
  /** ISO time. */
  startedAt: string;
  /** `working` while a file of the run is queued or being made; else `done`, or `failed` when one failed. */
  state: 'working' | 'done' | 'failed';
  total: number;
  ready: number;
  working: number;
  failed: number;
  /** The mean render progress of the files of the run, 0..100. */
  progress: number;
  /** The posts that moved to the new files so far. */
  postsSwapped: number;
  /** The credits that the failed files of the run gave back (each failed file gives its own back once). */
  failedCredits: number;
}

export interface CoverRedoState {
  /** True when a file to redo exists (`total` > 0). */
  outOfDate: boolean;
  /** The files that the redo makes again (no `skipReason`). */
  total: number;
  /** The files to make again per group; every group is present, 0 when it has none. */
  counts: Record<CoverRedoKind, number>;
  /** The video credits of the whole redo (one charge). 0: the redo is free. */
  credits: number;
  /** The video credits left (plan count and packs); null: no limit. */
  creditsAvailable: number | null;
  cover: {
    /** The cover of the song now. */
    currentUrl: string | null;
    /** The old cover: its file while it exists, else the newest out-of-date cover crop or image. */
    previousUrl: string | null;
  };
  /** Every out-of-date file, the skipped ones too (a file that is being made now has `skipReason: 'busy'`). */
  items: CoverRedoItem[];
  /** The posts that did not go out and use an out-of-date file. */
  scheduledPosts: CoverRedoPost[];
  /** The posts that went out with an out-of-date file. They stay as they are. */
  publishedPostCount: number;
  /** The newest redo of the song, or null when the song had none. */
  run: CoverRedoRun | null;
  /** The Free and trial files carry the Music24 watermark (the plan of the workspace now). */
  watermark: boolean;
}

export interface CoverRedoRequest {
  /** The groups to make again; absent: every group. */
  kinds?: CoverRedoKind[];
  /** True: the posts that did not go out move to the new files when each file is ready. */
  swapPosts: boolean;
  dryRun?: boolean;
  /** The credits of the dry run: required when the redo costs credits. */
  expectedCredits?: number;
}

export interface CoverRedoResult {
  runId: string | null;
  /** The credits taken. */
  credits: number;
  /** The new kit asset versions and reels that are queued. */
  queued: number;
  skipped: Array<{ uuid: string; reason: CoverRedoSkipReason }>;
  /** The posts that move to the new files when they are ready (0 when `swapPosts` is false). */
  postsToSwap: number;
}

/** A redo file of a chosen group that is not skipped. Pure. */
export function coverRedoItemsToMake(
  items: readonly CoverRedoItem[],
  kinds?: readonly CoverRedoKind[]
): CoverRedoItem[] {
  return items.filter((item) => item.skipReason === null && (kinds === undefined || kinds.includes(item.kind)));
}

/** The credits of the chosen groups. Pure. */
export function coverRedoCredits(items: readonly CoverRedoItem[], kinds?: readonly CoverRedoKind[]): number {
  return coverRedoItemsToMake(items, kinds).reduce((sum, item) => sum + item.credits, 0);
}
